/**
 * Gradient Preprocessing
 *
 * Pre-compute gradient visualization data for track maps.
 * Normalizes telemetry values for color mapping.
 */

import {
  PreprocessedDataPoint,
  LapComparisonCache,
  GradientMetricType,
  PreprocessedGradientPoint,
  PreprocessedLapGradient,
} from './types'

/**
 * Preprocess gradient data for track map visualization
 * Uses already-preprocessed lap comparison data
 */
export function preprocessGradientData(
  comparisonCache: LapComparisonCache,
  metric: GradientMetricType,
  lapColorMap: Record<string, string>
): PreprocessedLapGradient[] {
  if (comparisonCache.laps.length === 0) return []

  const lapGradients: PreprocessedLapGradient[] = []

  // First pass: collect all raw values to find global min/max
  let globalMin = Infinity
  let globalMax = -Infinity

  for (const lap of comparisonCache.laps) {
    for (const point of lap.dataPoints) {
      const rawValue = extractMetricValue(point, metric)
      if (rawValue !== undefined && !isNaN(rawValue)) {
        globalMin = Math.min(globalMin, rawValue)
        globalMax = Math.max(globalMax, rawValue)
      }
    }
  }

  // Handle edge case where all values are the same
  if (globalMin === globalMax) {
    globalMax = globalMin + 1
  }

  // Second pass: normalize values
  for (const lap of comparisonCache.laps) {
    const points: PreprocessedGradientPoint[] = []
    const distances: number[] = []

    for (const dataPoint of lap.dataPoints) {
      const rawValue = extractMetricValue(dataPoint, metric)

      if (rawValue !== undefined && !isNaN(rawValue)) {
        // Special normalization for time drift (centered around 0)
        let normalizedValue: number
        if (metric === 'timeDrift') {
          normalizedValue = normalizeTimeDrift(rawValue)
        } else if (metric === 'acceleration') {
          // Acceleration is centered around 0 (brake negative, accel positive)
          normalizedValue = normalizeAcceleration(rawValue)
        } else {
          normalizedValue = (rawValue - globalMin) / (globalMax - globalMin)
        }

        points.push({
          lat: dataPoint.lat,
          lng: dataPoint.lng,
          distance: dataPoint.distance,
          time: dataPoint.time,
          normalizedValue: Math.max(0, Math.min(1, normalizedValue)),
          rawValue,
        })
        distances.push(dataPoint.distance)
      }
    }

    lapGradients.push({
      lapId: lap.lapId,
      lapNumber: lap.lapNumber,
      color: lapColorMap[lap.lapId] ?? '#3B82F6',
      points,
      distanceRange: {
        min: distances.length > 0 ? Math.min(...distances) : 0,
        max: distances.length > 0 ? Math.max(...distances) : 0,
      },
    })
  }

  return lapGradients
}

/**
 * Extract metric value from preprocessed point
 */
function extractMetricValue(
  point: PreprocessedDataPoint,
  metric: GradientMetricType
): number | undefined {
  switch (metric) {
    case 'speed':
      return point.speed
    case 'timeDrift':
      return point.timeDrift
    case 'coasting':
      return point.coasting
    case 'leanAngle':
      return point.leanAngle !== undefined
        ? Math.abs(point.leanAngle)
        : undefined
    case 'lateralG':
      return point.lateralG !== undefined ? Math.abs(point.lateralG) : undefined
    case 'acceleration':
      return point.acceleration
    case 'brake':
      // Brake is negative acceleration
      return point.acceleration !== undefined && point.acceleration < 0
        ? Math.abs(point.acceleration)
        : 0
    case 'throttle':
      // Throttle is positive acceleration
      return point.acceleration !== undefined && point.acceleration > 0
        ? point.acceleration
        : 0
    default:
      return undefined
  }
}

/**
 * Normalize time drift with stronger contrast than the original but smooth transitions.
 * Uses a power curve (t^0.65) to push colors toward vivid red/green faster than linear
 * while avoiding the abrupt jumps that sqrt caused.
 * Neutral zone (±0.5 ms/m) stays in the yellow range; full saturation at ~6.5 ms/m.
 */
function normalizeTimeDrift(rate: number): number {
  // Neutral zone: near-zero rates stay in yellow/gold range
  if (rate >= -0.5 && rate <= 0.5) {
    return 0.5 + (rate / 0.5) * 0.05
  }
  // Losing time (positive rate) - power curve for faster color saturation
  if (rate > 0.5) {
    const t = Math.min(1, (rate - 0.5) / 6)
    return 0.55 + Math.pow(t, 0.65) * 0.45
  }
  // Gaining time (negative rate) - power curve for faster color saturation
  const t = Math.min(1, (Math.abs(rate) - 0.5) / 6)
  return 0.45 - Math.pow(t, 0.65) * 0.45
}

/**
 * Normalize acceleration value (centered around 0)
 */
function normalizeAcceleration(accel: number): number {
  // Map -2g to +2g to 0-1 range with 0g at 0.5
  const clampedAccel = Math.max(-2, Math.min(2, accel))
  return (clampedAccel + 2) / 4
}
